import axiosClient from '../clients/axiosClient';
import { getFaroSessionId, trackFaroEvent } from './faro';

type AnalyticsValue = string | number | boolean | null | undefined;

export type AnalyticsAttributes = Record<string, AnalyticsValue>;

const DEFAULT_DOMAIN = 'novaquantis.user_action';

function normalizeAttributes(
  attributes: AnalyticsAttributes,
): {
  payloadAttributes: Record<string, string | number | boolean | null>;
  faroAttributes: Record<string, string | number | boolean | undefined>;
} {
  const payloadAttributes: Record<string, string | number | boolean | null> =
    {};
  const faroAttributes: Record<string, string | number | boolean | undefined> =
    {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined) continue;
    payloadAttributes[key] = value;
    faroAttributes[key] =
      value === null ? 'null' : (value as string | number | boolean);
  }
  const sessionId = getFaroSessionId();
  payloadAttributes.session_id =
    (payloadAttributes.session_id as string | undefined) ?? sessionId;
  faroAttributes.session_id =
    (faroAttributes.session_id as string | undefined) ??
    (payloadAttributes.session_id as string);
  return { payloadAttributes, faroAttributes };
}

function buildPayload(
  name: string,
  attributes: AnalyticsAttributes,
  domain = DEFAULT_DOMAIN,
) {
  const { payloadAttributes } = normalizeAttributes(attributes);
  return { name, domain, attributes: payloadAttributes };
}

export async function sendAnalyticsEvent(
  name: string,
  attributes: AnalyticsAttributes = {},
  domain = DEFAULT_DOMAIN,
) {
  const { payloadAttributes, faroAttributes } = normalizeAttributes(attributes);
  trackFaroEvent(name, faroAttributes, domain);
  await axiosClient.post('/api/pulse/ingest', {
    name,
    domain,
    attributes: payloadAttributes,
  });
}

export async function sendPublicAnalyticsEvent(
  name: string,
  attributes: AnalyticsAttributes = {},
  domain = DEFAULT_DOMAIN,
) {
  const { payloadAttributes, faroAttributes } = normalizeAttributes(attributes);
  trackFaroEvent(name, faroAttributes, domain);
  await axiosClient.post('/api/pulse/public/ingest', {
    name,
    domain,
    attributes: payloadAttributes,
  });
}

export function sendPublicAnalyticsEventBeacon(
  name: string,
  attributes: AnalyticsAttributes = {},
  domain = DEFAULT_DOMAIN,
) {
  const { faroAttributes } = normalizeAttributes(attributes);
  trackFaroEvent(name, faroAttributes, domain);
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
    void sendPublicAnalyticsEvent(name, attributes, domain).catch(() => {});
    return;
  }
  const payload = buildPayload(name, attributes, domain);
  const blob = new Blob([JSON.stringify(payload)], {
    type: 'application/json',
  });
  navigator.sendBeacon('/api/pulse/public/ingest', blob);
}

export function sendAnalyticsEventBeacon(
  name: string,
  attributes: AnalyticsAttributes = {},
  domain = DEFAULT_DOMAIN,
) {
  const { faroAttributes } = normalizeAttributes(attributes);
  trackFaroEvent(name, faroAttributes, domain);
  const token = axiosClient.defaults.headers.common.Authorization;
  if (typeof token !== 'string' || !token.startsWith('Bearer ')) {
    sendPublicAnalyticsEventBeacon(name, attributes, domain);
    return;
  }
  void sendAnalyticsEvent(name, attributes, domain).catch(() => {
    sendPublicAnalyticsEventBeacon(name, attributes, domain);
  });
}

export async function trackAnalyticsEvent(
  name: string,
  attributes: AnalyticsAttributes = {},
  options: { authenticated?: boolean; domain?: string } = {},
) {
  const domain = options.domain ?? DEFAULT_DOMAIN;
  if (options.authenticated) {
    await sendAnalyticsEvent(name, attributes, domain);
    return;
  }
  await sendPublicAnalyticsEvent(name, attributes, domain);
}
